import api from "@/app/api";
import apiCache from "@/app/caching/apiCache";
import CreditTermsCache from "@/app/caching/caches/creditTermsCache";
import DeliveryAreasCache from "@/app/caching/caches/deliveryAreasCache";
import GroupsCache from "@/app/caching/caches/groupsCache";
import LeadsCache from "@/app/caching/caches/leadsCache";
import OutletRatingsCache from "@/app/caching/caches/outletRatingsCache";
import OutletSummariesCache from "@/app/caching/caches/outletSummariesCache";
import SalesAreasCache from "@/app/caching/caches/salesAreasCache";
import SalesCodesCache from "@/app/caching/caches/salesCodeCache";
import { BrewManCannotFindEntityError, BrewManError } from "@/app/errors";
import { EnumDocumentContextType } from "@/app/services/generated/enums/enumDocumentContextType.generated";
import { EnumSalesVisitAssociatedEntityType } from "@/app/services/generated/enums/enumSalesVisitAssociatedEntityType.generated";
import type { Group } from "@/app/services/generated/groupService";
import type { Lead } from "@/app/services/generated/leadService";
import {
  OrderFilter,
  orderService,
} from "@/app/services/generated/orderService";
import type {
  Outlet,
  OutletSummary,
} from "@/app/services/generated/outletService";
import { outletService } from "@/app/services/generated/outletService";
import type { PlannedSalesVisit } from "@/app/services/generated/salesvisitService";
import {
  PlannedSalesVisitFilter,
  SalesVisitAssociatedEntityFilter,
  SalesVisitLog,
  SalesVisitLogFilter,
  salesVisitService,
} from "@/app/services/generated/salesvisitService";
import Guid from "@/app/types/common/guid";
import { withLoading } from "@/components/composables/componentTools";
import { makeDeepRef } from "@/components/composables/reactivityTypes";
import type { LocalDate } from "@js-joda/core";
import type { Ref } from "vue";
import { watch, computed } from "vue";
import messages, {
  appendOrdinalSuffix,
  translate,
  translateWithParams,
} from "@/plugins/i18n";
import { EnumBmEntityType } from "@/app/services/generated/enums/enumBmEntityType.generated";
import { ActivityLogType } from "@/components/pages/outlet/crm/subcomponents/ActivityLogCard.vue";
import { contactsMenu } from "@/app/ui/navigation/contactsMenu";
import { salesMenu } from "@/app/ui/navigation/salesMenu";
import { EnumTaskRepeatBasis } from "@/app/services/generated/enums/enumTaskRepeatBasis.generated";
import { EnumDayOfWeek } from "@/app/services/generated/enums/enumDayOfWeek.generated";
import { showConfirmationDialog } from "@/components/composables/promptDialogs";
import { EnumDialogType } from "@/components/dialogs/DialogTypes";
import { customColoursLight } from "@/plugins/vuetify";
import type { Location } from "vue-router";
import { showModalDialog } from "@/components/platform/common/showModalDialog";
import CompleteVisitDialog from "@/components/pages/orders/salesvisits/components/CompleteVisitDialog.vue";
import { openMapView } from "@da/components/mixins/BmExternalLinks";
import Key from "@/app/types/common/key";

export type SalesVisitWithEntity = {
  salesVisit: PlannedSalesVisit;
  entity: AssociatedEntityDetails;
  sequence?: number;
};

export interface AssociatedEntityDetails {
  id: Guid;
  name: string;
  code: string | null;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
  addressLine4: string;
  addressLine5: string;
  addressLine6: string;
  addressPostcode: string;
  telephoneNumber: string;
  emailAddress: string;
  salesAreaId: Guid | null;
  salesCodeId: Guid | null;
  entityType: EnumSalesVisitAssociatedEntityType;
  latitude: number | null;
  longitude: number | null;
}

export function getRepeatBasisText(
  plannedSalesVisit: PlannedSalesVisit,
  basis?: EnumTaskRepeatBasis
) {
  const currentBasis = basis ?? plannedSalesVisit.repeatBasis;
  const dayOfWeekValue = plannedSalesVisit.date?.dayOfWeek().value();

  const dayOfWeekTranslated = dayOfWeekValue
    ? EnumDayOfWeek.getByKey(dayOfWeekValue).translation
    : "";

  return translate(Key.fromString(currentBasis.translationKey), {
    day: dayOfWeekTranslated,
    repeatParameter: plannedSalesVisit.repeatParameter,
    ordinalNumber: appendOrdinalSuffix(plannedSalesVisit.repeatParameter),
  }).toString();
}

export function getDetailsFromOutlet(entity: Readonly<OutletSummary>) {
  return {
    id: entity.id,
    name: entity.displayName,
    code: entity.code,
    addressLine1: entity.mainAddressLine1,
    addressLine2: entity.mainAddressLine2,
    addressLine3: entity.mainAddressLine3,
    addressLine4: entity.mainAddressLine4,
    addressLine5: entity.mainAddressLine5,
    addressLine6: entity.mainAddressLine6,
    addressPostcode: entity.mainAddressPostcode,
    telephoneNumber: entity.telephoneNumber,
    emailAddress: entity.emailAddress,
    salesAreaId: entity.salesAreaId,
    salesCodeId: entity.salesCodeId,
    entityType: EnumSalesVisitAssociatedEntityType.outlet,
    latitude: entity.mainLatitude,
    longitude: entity.mainLongitude,
  };
}

export function getDetailsFromGroup(entity: Readonly<Group>) {
  return {
    id: entity.id,
    name: entity.name,
    code: entity.readOnly.code,
    addressLine1: entity.mainAddressLine1,
    addressLine2: entity.mainAddressLine2,
    addressLine3: entity.mainAddressLine3,
    addressLine4: entity.mainAddressLine4,
    addressLine5: entity.mainAddressLine5,
    addressLine6: entity.mainAddressLine6,
    addressPostcode: entity.mainAddressPostcode,
    telephoneNumber: entity.telephoneNumber,
    emailAddress: entity.emailAddress,
    salesAreaId: null,
    salesCodeId: entity.salesCodeId,
    entityType: EnumSalesVisitAssociatedEntityType.group,
    latitude: entity.readOnly.mainLatitude,
    longitude: entity.readOnly.mainLongitude,
  };
}

export function getDetailsFromLead(entity: Readonly<Lead>) {
  return {
    id: entity.id,
    name: entity.businessName,
    code: null,
    addressLine1: entity.addressLine1,
    addressLine2: entity.addressLine2,
    addressLine3: entity.addressLine3,
    addressLine4: entity.addressLine4,
    addressLine5: entity.addressLine5,
    addressLine6: entity.addressLine6,
    addressPostcode: entity.addressPostcode,
    telephoneNumber: entity.telephoneNumber,
    emailAddress: entity.emailAddress,
    salesAreaId: entity.salesAreaId,
    salesCodeId: entity.salesCodeId,
    entityType: EnumSalesVisitAssociatedEntityType.lead,
    latitude: entity.readOnly.latitude,
    longitude: entity.readOnly.longitude,
  };
}

export function getSalesVisitEntityColour(
  entityType: EnumSalesVisitAssociatedEntityType
) {
  switch (entityType) {
    case EnumSalesVisitAssociatedEntityType.outlet:
      return customColoursLight.mediumBlue;
    case EnumSalesVisitAssociatedEntityType.group:
      return customColoursLight.darkBlue;
    case EnumSalesVisitAssociatedEntityType.lead:
      return customColoursLight.lightBlue;
    default:
      return null;
  }
}

export function getSalesVisitEntityLocation(
  entityType: EnumSalesVisitAssociatedEntityType,
  entityId: Guid
): Location | undefined {
  switch (entityType) {
    case EnumSalesVisitAssociatedEntityType.outlet:
      return contactsMenu.viewOutlet(entityId);
    case EnumSalesVisitAssociatedEntityType.group:
      return contactsMenu.viewOutletGroup(entityId);
    case EnumSalesVisitAssociatedEntityType.lead:
      return salesMenu.leads.viewLead(entityId);
  }
}

export function joinPlannedVisitWithAssociatedEntity(
  visit: PlannedSalesVisit,
  outlets: OutletSummariesCache,
  groups: GroupsCache,
  leads: LeadsCache,
  sequence?: number
): SalesVisitWithEntity {
  switch (visit.associatedEntityType) {
    case EnumSalesVisitAssociatedEntityType.outlet: {
      const outlet = outlets.getById(visit.associatedEntityId);

      if (!outlet) {
        throw new BrewManCannotFindEntityError(
          EnumBmEntityType.outlet,
          visit.associatedEntityId
        );
      }

      return {
        salesVisit: visit,
        entity: getDetailsFromOutlet(outlet),
        sequence: visit.readOnly.doesFitInRoute ? sequence : undefined,
      };
    }
    case EnumSalesVisitAssociatedEntityType.group: {
      const group = groups.getById(visit.associatedEntityId);

      if (!group) {
        throw new BrewManCannotFindEntityError(
          EnumBmEntityType.outletGroup,
          visit.associatedEntityId
        );
      }

      return {
        salesVisit: visit,
        entity: getDetailsFromGroup(group),
        sequence: visit.readOnly.doesFitInRoute ? sequence : undefined,
      };
    }
    case EnumSalesVisitAssociatedEntityType.lead: {
      const lead = leads.getById(visit.associatedEntityId);

      if (!lead) {
        throw new BrewManCannotFindEntityError(
          EnumBmEntityType.lead,
          visit.associatedEntityId
        );
      }

      return {
        salesVisit: visit,
        entity: getDetailsFromLead(lead),
        sequence: visit.readOnly.doesFitInRoute ? sequence : undefined,
      };
    }
    default:
      throw new BrewManError(
        translateWithParams("sales_visit_unknown_entity_type", {
          entity: visit.associatedEntityType.value,
          id: visit.associatedEntityId.toString(),
        }).toString()
      );
  }
}

export async function deleteVisit(id: Guid) {
  const shouldDeleteVisit = await showConfirmationDialog(
    messages.prompt_confirm_title,
    messages.sales_visit_delete_visit_prompt,
    {
      type: EnumDialogType.error,
    }
  );

  if (shouldDeleteVisit) {
    await salesVisitService.deletePlannedSalesVisit(api.tenantId, id);
  }

  return shouldDeleteVisit;
}

export async function createVisitLogFromVisit(visit: PlannedSalesVisit) {
  const newVisitLog = new SalesVisitLog();
  newVisitLog.header.id = Guid.create();
  newVisitLog.header.date = visit.date;
  newVisitLog.header.associatedEntityType = visit.associatedEntityType;
  newVisitLog.header.associatedEntityId = visit.associatedEntityId;
  newVisitLog.header.userId = visit.userId;

  await salesVisitService.saveSalesVisitLog(api.tenantId, newVisitLog);

  return await salesVisitService.getSalesVisitLog(
    api.tenantId,
    newVisitLog.header.id
  );
}

export async function completeSalesVisitLog(
  plannedSalesVisit: PlannedSalesVisit | null
) {
  if (plannedSalesVisit === null) {
    throw new BrewManError(messages.sales_visit_planned_sales_visit_not_found);
  }

  if (plannedSalesVisit?.repeatBasis === EnumTaskRepeatBasis.none) {
    if (
      await showConfirmationDialog(
        messages.prompt_confirm_title,
        messages.sales_visit_complete_confirm
      )
    ) {
      await salesVisitService.deletePlannedSalesVisit(
        api.tenantId,
        plannedSalesVisit.id
      );
    }
  } else {
    await showModalDialog<void, typeof CompleteVisitDialog>(
      CompleteVisitDialog,
      {
        props: {
          plannedSalesVisitId: plannedSalesVisit!.id,
        },
      }
    );
  }
}

export function openVisitNavigation(
  salesVisitWithEntity: SalesVisitWithEntity
) {
  if (
    salesVisitWithEntity.entity.latitude === null ||
    salesVisitWithEntity.entity.longitude == null
  ) {
    openMapView(
      salesVisitWithEntity.entity.addressLine2,
      salesVisitWithEntity.entity.addressLine3,
      salesVisitWithEntity.entity.addressPostcode
    );
    return;
  }
  openMapView(
    salesVisitWithEntity.entity.latitude.toString(),
    salesVisitWithEntity.entity.longitude.toString()
  );
}

export async function getPlannedVisitFromVisitLog(
  salesVisitLog: SalesVisitLog
) {
  const filter = new PlannedSalesVisitFilter();
  filter.fromDate = salesVisitLog.header.date;
  filter.toDate = salesVisitLog.header.date;

  const entityFilter = new SalesVisitAssociatedEntityFilter();
  entityFilter.entityId = salesVisitLog.header.associatedEntityId;
  entityFilter.entityType = salesVisitLog.header.associatedEntityType;

  filter.limitAssociatedEntities = [entityFilter];
  filter.limitUserIds = [salesVisitLog.header.userId];

  const result = await salesVisitService.getPlannedSalesVisitsByFilter(
    api.tenantId,
    filter
  );

  return result.matchingCount === 1 ? result.plannedSalesVisits[0] : null;
}

export async function getPlannedVisitByDateAndUser(
  fromDate: LocalDate,
  toDate: LocalDate,
  userIds: Guid[]
) {
  const filter = new PlannedSalesVisitFilter();
  filter.fromDate = fromDate;
  filter.toDate = toDate;
  filter.limitUserIds = userIds;

  return await salesVisitService.getPlannedSalesVisitsByFilter(
    api.tenantId,
    filter
  );
}

export function getRoutingLocation(
  associatedEntityType: EnumSalesVisitAssociatedEntityType,
  associatedEntityId: Guid
) {
  switch (associatedEntityType) {
    case EnumSalesVisitAssociatedEntityType.outlet:
      return contactsMenu.viewOutlet(associatedEntityId);
    case EnumSalesVisitAssociatedEntityType.group:
      return contactsMenu.viewOutletGroup(associatedEntityId);
    case EnumSalesVisitAssociatedEntityType.lead:
      return salesMenu.leads.viewLead(associatedEntityId);
    default: {
      throw new BrewManError(
        translateWithParams("sales_visit_unknown_entity_type", {
          entity: associatedEntityType.value,
          id: associatedEntityId.toString(),
        }).toString()
      );
    }
  }
}

export default function useSalesVisitData(
  associatedEntityType: Ref<Readonly<EnumSalesVisitAssociatedEntityType>>,
  associatedEntityId: Ref<Readonly<Guid>>,
  userId: Ref<Readonly<Guid>>,
  date: Ref<Readonly<LocalDate>>
) {
  const lastOrderDate = makeDeepRef<LocalDate | null>(null);
  const lastVisitDate = makeDeepRef<LocalDate | null>(null);

  const associatedOutlet = makeDeepRef<Outlet | null>(null);
  const associatedGroup = makeDeepRef<Group | null>(null);
  const associatedLead = makeDeepRef<Lead | null>(null);

  const loadLastOrder = async () => {
    if (
      associatedEntityType.value === EnumSalesVisitAssociatedEntityType.lead
    ) {
      lastOrderDate.value = null;
      return;
    }

    const filter = new OrderFilter();

    if (
      associatedEntityType.value === EnumSalesVisitAssociatedEntityType.outlet
    ) {
      filter.singleOutletId = associatedEntityId.value;
    } else if (
      associatedEntityType.value === EnumSalesVisitAssociatedEntityType.group
    ) {
      filter.singleGroupId = associatedEntityId.value;
    }

    filter.maximumResults = 1;

    const result = await orderService.getOrdersByFilter(
      api.tenantId,
      filter,
      false
    );

    lastOrderDate.value =
      result.matchingCount === 0 ? null : result.orders[0].header.despatchDate;
  };

  const associatedEntityFilter = computed(() => {
    const entityFilter = new SalesVisitAssociatedEntityFilter();
    entityFilter.entityId = associatedEntityId.value!;

    switch (associatedEntityType.value) {
      case EnumSalesVisitAssociatedEntityType.outlet:
        entityFilter.entityType = EnumSalesVisitAssociatedEntityType.outlet;

        break;
      case EnumSalesVisitAssociatedEntityType.group:
        entityFilter.entityType = EnumSalesVisitAssociatedEntityType.group;

        break;
      case EnumSalesVisitAssociatedEntityType.lead:
        entityFilter.entityType = EnumSalesVisitAssociatedEntityType.lead;
        break;
      default: {
        throw new BrewManError(
          `Unrecognized entity type: '${associatedEntityType.value}'`
        );
      }
    }

    return entityFilter;
  });

  const loadLatestVisitLog = async () => {
    const filter = new SalesVisitLogFilter();
    filter.limitAssociatedEntities = [associatedEntityFilter.value];
    filter.limitUserIds = [userId.value];
    filter.toDate = date.value.minusDays(1);
    filter.maximumResults = 1;

    const result = await salesVisitService.getSalesVisitLogsByFilter(
      api.tenantId,
      filter
    );

    lastVisitDate.value =
      result.matchingCount === 0 ? null : result.salesVisitLogs[0].header.date;
  };

  const cache = apiCache.buildCacheSet({
    outlets: OutletSummariesCache,
    groups: GroupsCache,
    leads: LeadsCache,
    salesCodes: SalesCodesCache,
    salesAreas: SalesAreasCache,
    deliveryAreas: DeliveryAreasCache,
    creditTerms: CreditTermsCache,
    outletRatings: OutletRatingsCache,
  });

  const documentStoreContext = computed(() => {
    switch (associatedEntityType.value) {
      case EnumSalesVisitAssociatedEntityType.outlet:
        return EnumDocumentContextType.outlet;
      case EnumSalesVisitAssociatedEntityType.group:
        return EnumDocumentContextType.outletGroup;
      case EnumSalesVisitAssociatedEntityType.lead:
        return EnumDocumentContextType.lead;
      default: {
        throw new BrewManError(
          translateWithParams("sales_visit_unknown_entity_type", {
            entity: associatedEntityType.value.value,
            id: associatedEntityId.toString(),
          }).toString()
        );
      }
    }
  });

  const logCommunicationType = computed(() => {
    switch (associatedEntityType.value) {
      case EnumSalesVisitAssociatedEntityType.outlet:
        return EnumBmEntityType.outlet;
      case EnumSalesVisitAssociatedEntityType.group:
        return EnumBmEntityType.outletGroup;
      case EnumSalesVisitAssociatedEntityType.lead:
        return EnumBmEntityType.lead;
      default: {
        throw new BrewManError(
          translateWithParams("sales_visit_unknown_entity_type", {
            entity: associatedEntityType.value.value,
            id: associatedEntityId.toString(),
          }).toString()
        );
      }
    }
  });

  const activityLogType = computed(() => {
    switch (associatedEntityType.value) {
      case EnumSalesVisitAssociatedEntityType.outlet:
        return ActivityLogType.outlet;
      case EnumSalesVisitAssociatedEntityType.group:
        return null;
      case EnumSalesVisitAssociatedEntityType.lead:
        return ActivityLogType.lead;
      default: {
        throw new BrewManError(
          translateWithParams("sales_visit_unknown_entity_type", {
            entity: associatedEntityType.value.value,
            id: associatedEntityId.toString(),
          }).toString()
        );
      }
    }
  });

  const refresh = withLoading(async () => {
    if (
      associatedEntityId.value === null ||
      associatedEntityId.value.isEmpty()
    ) {
      return;
    }

    await Promise.all([cache.update(), loadLastOrder(), loadLatestVisitLog()]);

    associatedOutlet.value =
      associatedEntityType.value === EnumSalesVisitAssociatedEntityType.outlet
        ? await outletService.getOutlet(api.tenantId, associatedEntityId.value)
        : null;

    associatedGroup.value =
      associatedEntityType.value === EnumSalesVisitAssociatedEntityType.group
        ? (cache.groups.getById(associatedEntityId.value) as Group)
        : null;

    associatedLead.value =
      associatedEntityType.value === EnumSalesVisitAssociatedEntityType.lead
        ? (cache.leads.getById(associatedEntityId.value) as Lead)
        : null;
  });

  watch([associatedEntityId, userId], async () => await refresh(), {
    immediate: true,
  });

  return {
    lastOrderDate,
    lastVisitDate,
    associatedOutlet,
    associatedGroup,
    associatedLead,
    documentStoreContext,
    cache,
    logCommunicationType,
    activityLogType,
    refresh,
  };
}
